import { FormInstance } from 'antd'
import { endOfMonth } from '../../../config/dayjsConfig'
import {
  DeliveryChannel,
  DeliverySubscription,
  DeliverySubscriptionType
} from '../../../types/deliverySubscription/DeliverySubscription'
import { useCreateDeliverySubscription } from '../queries/useCreateDeliverySubscription'
import { useSendDeliverySubscription } from '../queries/useSendDeliverySubscription'
import { useUpdateDeliverySubscription } from '../queries/useUpdateDeliverySubscription'
import { DeliverySubscriptionFormData } from '../types'

interface UseDeliverySubscriptionSaveParams {
  subscriptionType: DeliverySubscriptionType
  deliverySubscription?: DeliverySubscription
  form: FormInstance<DeliverySubscriptionFormData>
  onSuccess: () => void
}

export const useDeliverySubscriptionSave = ({
  subscriptionType,
  deliverySubscription,
  form,
  onSuccess
}: UseDeliverySubscriptionSaveParams) => {
  const { mutateAsync: createSubscription, isPending: createPending } = useCreateDeliverySubscription()
  const { mutateAsync: updateSubscription, isPending: updatePending } = useUpdateDeliverySubscription()
  const { mutateAsync: sendSubscription } = useSendDeliverySubscription()

  const transformPayload = (values: DeliverySubscriptionFormData): DeliverySubscription => {
    const isCustomReport = subscriptionType === DeliverySubscriptionType.CUSTOM_REPORT
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { type: _initiatorType, ...initiatorFields } = values.initiator ?? {}
    return {
      ...values,
      initiator: initiatorFields,
      channel:
        subscriptionType === DeliverySubscriptionType.ACCOUNTOR_SCORECARD
          ? DeliveryChannel.FINADECK
          : DeliveryChannel.EMAIL,
      type: subscriptionType,
      recipients: values.recipients || null,
      params: {
        ...values?.params,
        endDate: values?.params?.endDate ? endOfMonth(values.params.endDate) : undefined,
        printVersion: isCustomReport ? values.params?.printVersion ?? 'v2' : undefined
      }
    }
  }

  const handleSave = () => {
    form
      .validateFields()
      .then(async formData => {
        const payload = transformPayload(formData)
        if (deliverySubscription) {
          await updateSubscription({ id: deliverySubscription.id, data: payload })
        } else {
          await createSubscription(payload)
        }
        onSuccess()
      })
      .catch(e => console.log(e))
  }

  const handleSaveAndSend = () => {
    form
      .validateFields()
      .then(async formData => {
        const payload = transformPayload(formData)
        let savedId: number
        if (deliverySubscription) {
          const updated = await updateSubscription({ id: deliverySubscription.id, data: payload })
          savedId = updated.id
        } else {
          const created = await createSubscription(payload)
          savedId = created.id
        }
        await sendSubscription(savedId)
        onSuccess()
      })
      .catch(e => console.log(e))
  }

  return {
    handleSave,
    handleSaveAndSend,
    isSaving: createPending || updatePending
  }
}
